(() => {
  'use strict';

  const LF_EVENT_ADDONS = 'lf:addons-change';
  const LF_EVENT_GIFT = 'lf:gift-change';
  const LF_EVENT_VARIANT = 'lf:variant-selection';

  const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseJson = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('LF product data could not be parsed.', error);
      return fallback;
    }
  };

  const emitDocumentEvent = (name, detail) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  class LFProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.lfConnected) return;
      this.lfConnected = true;
      this.slides = Array.from(this.querySelectorAll('[data-lf-media-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-lf-thumbnail]'));
      this.gallerySlides = this.slides;
      this.visibleSlides = this.slides;
      this.maxImagesPerOption = Math.max(1, toNumber(this.dataset.maxImagesPerOption, 25));
      this.maxVideosPerOption = Math.max(1, toNumber(this.dataset.maxVideosPerOption, 5));
      this.activeMediaType = 'overview';
      this.counter = this.querySelector('[data-lf-media-counter]');
      this.stage = this.querySelector('[data-lf-gallery-stage]');
      this.thumbnailContainer = this.querySelector('[data-lf-thumbnails]');
      this.tabsContainer = this.querySelector('[data-lf-gallery-tabs]');
      this.previousButton = this.querySelector('[data-lf-gallery-previous]');
      this.nextButton = this.querySelector('[data-lf-gallery-next]');
      this.pointerStart = null;
      this.configureInlineVideoPlayback();

      const initialId = String(this.dataset.activeMediaId || '');
      this.index = 0;
      this.previousButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => this.activateMediaKey(thumbnail.dataset.mediaKey));
      });
      this.querySelectorAll('[data-lf-gallery-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.activateType(tab.dataset.lfGalleryTab));
      });
      this.slides.forEach((slide) => {
        slide.querySelectorAll('iframe').forEach((iframe) => {
          iframe.addEventListener('load', () => {
            if (slide.classList.contains('is-active')) this.playSlide(slide);
          });
        });
      });

      this.stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(1);
        }
      });
      this.stage?.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.pointerStart = event.clientX;
      });
      this.stage?.addEventListener('pointerup', (event) => {
        if (this.pointerStart === null) return;
        const distance = event.clientX - this.pointerStart;
        this.pointerStart = null;
        if (Math.abs(distance) < 48) return;
        this.move(distance < 0 ? 1 : -1);
      });
      this.stage?.addEventListener('pointercancel', () => { this.pointerStart = null; });
      this.applyGalleryKey(this.dataset.initialGalleryKey || '', initialId);
    }

    configureInlineVideoPlayback() {
      this.querySelectorAll('video.lf-pdp__media-video').forEach((video) => {
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('x5-playsinline', '');
        video.setAttribute('x5-video-player-type', 'h5-page');
        video.setAttribute('x5-video-player-fullscreen', 'false');
        video.setAttribute('disablepictureinpicture', '');
        if ('disablePictureInPicture' in video) video.disablePictureInPicture = true;
      });
    }

    move(direction) {
      if (this.visibleSlides.length < 2) return;
      this.show((this.index + direction + this.visibleSlides.length) % this.visibleSlides.length);
    }

    activateMedia(mediaId) {
      const index = this.visibleSlides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.show(index);
    }

    activateMediaKey(mediaKey) {
      const index = this.visibleSlides.findIndex((slide) => slide.dataset.mediaKey === String(mediaKey));
      if (index >= 0) this.show(index);
    }

    activateType(type) {
      if (!['overview', 'video'].includes(type)) return;
      const hasType = this.gallerySlides.some((slide) => this.matchesMediaType(slide, type));
      if (!hasType) return;
      this.activeMediaType = type;
      this.applyMediaTypeFilter();
    }

    isVideo(slide) {
      return ['video', 'external_video'].includes(slide?.dataset.mediaType);
    }

    isImage(slide) {
      return slide?.dataset.mediaType === 'image';
    }

    controlExternalVideo(slide, shouldPlay) {
      if (slide?.dataset.mediaType !== 'external_video') return;
      const host = String(slide.dataset.videoHost || '').toLowerCase();
      slide.querySelectorAll('iframe').forEach((iframe) => {
        if (!iframe.contentWindow) return;
        const source = String(iframe.src || '').toLowerCase();
        if (host === 'youtube' || source.includes('youtube.com') || source.includes('youtu.be')) {
          iframe.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: shouldPlay ? 'playVideo' : 'pauseVideo',
            args: []
          }), '*');
        } else if (host === 'vimeo' || source.includes('vimeo.com')) {
          iframe.contentWindow.postMessage(JSON.stringify({ method: shouldPlay ? 'play' : 'pause' }), '*');
        }
      });
    }

    pauseSlide(slide) {
      slide?.querySelectorAll('video').forEach((video) => video.pause());
      this.controlExternalVideo(slide, false);
    }

    playSlide(slide) {
      if (!this.isVideo(slide)) return;
      slide.querySelectorAll('video').forEach((video) => {
        const playRequest = video.play();
        if (playRequest) playRequest.catch(() => {});
      });
      this.controlExternalVideo(slide, true);
    }

    matchesMediaType(slide, type) {
      if (type === 'all') return true;
      return type === 'video' ? this.isVideo(slide) : !this.isVideo(slide);
    }

    normalizeGalleryKey(value) {
      return String(value || '').trim().toLowerCase();
    }

    filterForVariant(variant, preferredMediaId) {
      const optionPosition = toNumber(this.dataset.optionPosition);
      const optionValue = optionPosition > 0 ? variant?.options?.[optionPosition - 1] : '';
      const optionKey = this.normalizeGalleryKey(optionValue);
      const hasOptionGroup = Boolean(optionKey) && this.slides.some((slide) => {
        return this.normalizeGalleryKey(slide.dataset.lfGalleryGroup) === optionKey;
      });
      let galleryKey = optionKey;
      if (!hasOptionGroup && preferredMediaId) {
        const matchedSlide = this.slides.find((slide) => {
          const group = this.normalizeGalleryKey(slide.dataset.lfGalleryGroup);
          return String(slide.dataset.mediaId || '') === String(preferredMediaId)
            && group !== '__shared__'
            && group !== '__fallback__';
        });
        if (matchedSlide) galleryKey = this.normalizeGalleryKey(matchedSlide.dataset.lfGalleryGroup);
      }
      this.applyGalleryKey(galleryKey, preferredMediaId);
    }

    applyGalleryKey(value, preferredMediaId) {
      const galleryKey = this.normalizeGalleryKey(value);
      const hasConfiguredGroup = Boolean(galleryKey) && this.slides.some((slide) => {
        return this.normalizeGalleryKey(slide.dataset.lfGalleryGroup) === galleryKey;
      });
      const allowedGroups = new Set(hasConfiguredGroup
        ? ['__shared__', galleryKey]
        : ['__shared__', '__fallback__']);
      const seenMedia = new Set();
      let visibleImageCount = 0;
      let visibleVideoCount = 0;

      this.gallerySlides = this.slides.filter((slide) => {
        const group = this.normalizeGalleryKey(slide.dataset.lfGalleryGroup);
        const mediaId = String(slide.dataset.mediaId || slide.dataset.mediaKey || '');
        if (!allowedGroups.has(group) || seenMedia.has(mediaId)) return false;
        seenMedia.add(mediaId);
        if (this.isImage(slide)) {
          if (visibleImageCount >= this.maxImagesPerOption) return false;
          visibleImageCount += 1;
        }
        if (this.isVideo(slide)) {
          if (visibleVideoCount >= this.maxVideosPerOption) return false;
          visibleVideoCount += 1;
        }
        return true;
      });

      const hasVideo = this.gallerySlides.some((slide) => this.isVideo(slide));
      const hasOverview = this.gallerySlides.some((slide) => !this.isVideo(slide));
      if (this.tabsContainer) this.tabsContainer.hidden = !hasVideo;
      if (!hasVideo) {
        this.activeMediaType = 'all';
      } else if (this.activeMediaType !== 'video') {
        this.activeMediaType = hasOverview ? 'overview' : 'video';
      }

      this.applyMediaTypeFilter(preferredMediaId, galleryKey);
    }

    applyMediaTypeFilter(preferredMediaId, preferredGroup) {
      const tabsVisible = Boolean(this.tabsContainer && !this.tabsContainer.hidden);
      const mediaType = tabsVisible ? this.activeMediaType : 'all';
      this.visibleSlides = this.gallerySlides.filter((slide) => this.matchesMediaType(slide, mediaType));

      const visibleKeys = new Set(this.visibleSlides.map((slide) => slide.dataset.mediaKey));
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.hidden = !visibleKeys.has(thumbnail.dataset.mediaKey);
      });
      const visibleThumbnails = this.thumbnails.filter((thumbnail) => !thumbnail.hidden);
      visibleThumbnails.forEach((thumbnail, index) => {
        const mediaType = String(thumbnail.dataset.mediaType || 'media');
        const mediaTypeLabel = {
          image: 'Bild',
          video: 'Video',
          external_video: 'Externes Video',
          model: '3D-Modell',
          media: 'Medium'
        }[mediaType] || 'Medium';
        thumbnail.setAttribute('aria-label', `${mediaTypeLabel} ${index + 1} von ${visibleThumbnails.length} anzeigen`);
      });
      if (this.thumbnailContainer) {
        this.thumbnailContainer.hidden = !this.visibleSlides.length || (!tabsVisible && this.visibleSlides.length < 2);
      }

      let index = this.visibleSlides.findIndex((slide) => slide.dataset.mediaId === String(preferredMediaId || ''));
      if (index < 0 && preferredGroup) {
        index = this.visibleSlides.findIndex((slide) => {
          return this.normalizeGalleryKey(slide.dataset.lfGalleryGroup) === preferredGroup;
        });
      }
      this.show(Math.max(0, index));
    }

    show(index) {
      if (!this.visibleSlides.length) {
        this.slides.forEach((slide) => {
          slide.hidden = true;
          slide.classList.remove('is-active');
          this.pauseSlide(slide);
        });
        if (this.counter) this.counter.hidden = true;
        if (this.previousButton) this.previousButton.hidden = true;
        if (this.nextButton) this.nextButton.hidden = true;
        return;
      }
      this.index = Math.max(0, Math.min(index, this.visibleSlides.length - 1));
      const active = this.visibleSlides[this.index];
      this.slides.forEach((slide) => {
        const selected = slide === active;
        slide.hidden = !selected;
        slide.classList.toggle('is-active', selected);
        if (!selected) this.pauseSlide(slide);
      });
      this.playSlide(active);
      this.thumbnails.forEach((thumbnail) => {
        const selected = thumbnail.dataset.mediaKey === active.dataset.mediaKey;
        thumbnail.classList.toggle('is-active', selected);
        thumbnail.setAttribute('aria-current', String(selected));
        if (selected) this.scrollThumbnailIntoView(thumbnail);
      });
      this.querySelectorAll('[data-lf-gallery-tab]').forEach((tab) => {
        const type = tab.dataset.lfGalleryTab;
        const isActive = this.activeMediaType === type;
        const hasType = this.gallerySlides.some((slide) => this.matchesMediaType(slide, type));
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', String(isActive));
        tab.disabled = !hasType;
        tab.setAttribute('aria-disabled', String(!hasType));
      });
      this.dataset.activeMediaId = active.dataset.mediaId;
      const hasMultiple = this.visibleSlides.length > 1;
      if (this.counter) {
        this.counter.hidden = false;
        this.counter.textContent = `${this.index + 1} / ${this.visibleSlides.length}`;
      }
      if (this.previousButton) this.previousButton.hidden = !hasMultiple;
      if (this.nextButton) this.nextButton.hidden = !hasMultiple;
    }

    scrollThumbnailIntoView(thumbnail) {
      if (!this.thumbnailContainer || thumbnail.hidden) return;

      const containerRect = this.thumbnailContainer.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const isVertical = window.matchMedia('(min-width: 1200px)').matches;
      const offset = isVertical
        ? thumbnailRect.top < containerRect.top
          ? thumbnailRect.top - containerRect.top
          : thumbnailRect.bottom > containerRect.bottom
            ? thumbnailRect.bottom - containerRect.bottom
            : 0
        : thumbnailRect.left < containerRect.left
          ? thumbnailRect.left - containerRect.left
          : thumbnailRect.right > containerRect.right
            ? thumbnailRect.right - containerRect.right
            : 0;
      if (!offset) return;

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (isVertical) {
        this.thumbnailContainer.scrollTo({
          top: Math.max(0, this.thumbnailContainer.scrollTop + offset),
          behavior
        });
      } else {
        this.thumbnailContainer.scrollTo({
          left: Math.max(0, this.thumbnailContainer.scrollLeft + offset),
          behavior
        });
      }
    }
  }

  class LFVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.lfConnected) return;
      this.lfConnected = true;
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-lf-option-input]')) return;
        this.resolveSelection();
      });
    }

    initialize(variants, selectedVariant) {
      this.variants = Array.isArray(variants) ? variants : [];
      if (selectedVariant) this.syncInputs(selectedVariant.options || []);
      this.updateOptionAvailability();
    }

    isVariantPurchasable(variant) {
      const purchase = this.closest('lf-product-purchase');
      if (typeof purchase?.isVariantPurchasable === 'function') return purchase.isVariantPurchasable(variant);
      return Boolean(variant?.available);
    }

    getSelections() {
      return Array.from(this.querySelectorAll('[data-lf-option-position]')).map((fieldset) => {
        return fieldset.querySelector('[data-lf-option-input]:checked')?.value || '';
      });
    }

    syncInputs(options) {
      options.forEach((value, index) => {
        const fieldset = this.querySelector(`[data-lf-option-position="${index + 1}"]`);
        const input = Array.from(fieldset?.querySelectorAll('[data-lf-option-input]') || []).find((candidate) => candidate.value === value);
        if (input) input.checked = true;
        const label = this.querySelector(`[data-lf-option-label="${index + 1}"]`);
        if (label) label.textContent = value;
      });
    }

    resolveSelection() {
      if (!this.variants?.length) return;
      const selections = this.getSelections();
      for (let index = 0; index < selections.length; index += 1) {
        const viable = this.variants.filter((variant) => {
          if (!this.isVariantPurchasable(variant)) return false;
          for (let prior = 0; prior < index; prior += 1) {
            if (variant.options[prior] !== selections[prior]) return false;
          }
          return variant.options[index] === selections[index];
        });
        if (!viable.length) {
          const fallback = this.variants.find((variant) => {
            if (!this.isVariantPurchasable(variant)) return false;
            for (let prior = 0; prior < index; prior += 1) {
              if (variant.options[prior] !== selections[prior]) return false;
            }
            return true;
          });
          if (fallback) selections[index] = fallback.options[index];
        }
      }

      const variant = this.variants.find((candidate) => this.isVariantPurchasable(candidate)
        && candidate.options.every((value, index) => value === selections[index]))
        || this.variants.find((candidate) => this.isVariantPurchasable(candidate));
      if (!variant) return;
      this.syncInputs(variant.options || []);
      this.updateOptionAvailability();
      this.dispatchEvent(new CustomEvent(LF_EVENT_VARIANT, { bubbles: true, detail: { variant } }));
    }

    updateOptionAvailability() {
      if (!this.variants?.length) return;
      const selections = this.getSelections();
      const hideUnavailableOptions = this.closest('lf-product-purchase')?.dataset.hideUnavailableOptions === 'true';
      let visibleFieldsets = 0;

      this.querySelectorAll('[data-lf-option-position]').forEach((fieldset, fieldsetIndex) => {
        const filterByPriorOptions = fieldset.hasAttribute('data-lf-color-option') && fieldsetIndex > 0;
        let hasVisibleOption = false;
        fieldset.querySelectorAll('[data-lf-option-input]').forEach((input) => {
          const hasCombination = this.variants.some((variant) => {
            if (variant.options[fieldsetIndex] !== input.value) return false;
            for (let prior = 0; prior < fieldsetIndex; prior += 1) {
              if (variant.options[prior] !== selections[prior]) return false;
            }
            return true;
          });
          const viable = this.variants.some((variant) => {
            if (!this.isVariantPurchasable(variant) || variant.options[fieldsetIndex] !== input.value) return false;
            for (let prior = 0; prior < fieldsetIndex; prior += 1) {
              if (variant.options[prior] !== selections[prior]) return false;
            }
            return true;
          });
          input.disabled = !viable;
          const label = input.nextElementSibling;
          const hidden = hideUnavailableOptions ? !viable : filterByPriorOptions && !hasCombination;
          input.hidden = hidden;
          if (label) label.hidden = hidden;
          label?.classList.toggle('is-unavailable', !viable);
          label?.setAttribute('aria-disabled', String(!viable));
          if (!hidden) hasVisibleOption = true;
        });
        fieldset.hidden = hideUnavailableOptions && !hasVisibleOption;
        if (!fieldset.hidden) visibleFieldsets += 1;
      });
      this.hidden = hideUnavailableOptions && visibleFieldsets === 0;
    }
  }

  class LFProductAddons extends HTMLElement {
    connectedCallback() {
      if (this.lfConnected) return;
      this.lfConnected = true;
      this.bindControls();
    }

    bindControls() {
      if (this.lfControlsBound) return;
      this.lfControlsBound = true;
      this.querySelectorAll('[data-lf-addon-card]').forEach((card) => {
        const checkbox = card.querySelector('[data-lf-addon-checkbox]');
        const select = card.querySelector('[data-lf-addon-variant]');
        const quantity = card.querySelector('[data-lf-addon-quantity]');
        const quantityWrap = card.querySelector('[data-lf-addon-quantity-wrap]');

        checkbox?.addEventListener('change', () => {
          this.syncQuantity(card);
          if (!checkbox.checked) this.clearInventoryError(card);
          card.classList.toggle('is-selected', checkbox.checked);
          if (quantityWrap) quantityWrap.hidden = !checkbox.checked;
          this.dispatchChange();
        });
        select?.addEventListener('change', () => {
          const option = select.selectedOptions[0];
          const available = option?.dataset.available === 'true';
          checkbox.value = option?.value || '';
          checkbox.dataset.minQuantity = option?.dataset.minQuantity || '1';
          checkbox.dataset.maxQuantity = option?.dataset.maxQuantity || '0';
          checkbox.dataset.step = option?.dataset.step || '1';
          checkbox.disabled = !available;
          if (!available) checkbox.checked = false;
          this.syncQuantity(card);
          this.clearInventoryError(card);
          card.classList.toggle('is-selected', checkbox.checked);
          if (quantityWrap) quantityWrap.hidden = !checkbox.checked;
          const root = this.closest('lf-product-purchase');
          const price = card.querySelector('[data-lf-addon-price]');
          const compare = card.querySelector('[data-lf-addon-compare]');
          const availability = card.querySelector('[data-lf-addon-availability]');
          if (price) price.textContent = root?.formatMoney(toNumber(option?.dataset.price)) || '';
          const comparePrice = toNumber(option?.dataset.compareAtPrice);
          compare?.classList.toggle('is-hidden', comparePrice <= toNumber(option?.dataset.price));
          if (compare && comparePrice > 0) compare.textContent = root?.formatMoney(comparePrice) || '';
          if (availability) availability.textContent = available ? '' : 'Ausverkauft';
          this.dispatchChange();
        });
        card.querySelector('[data-lf-addon-quantity-decrease]')?.addEventListener('click', () => {
          const rule = this.getQuantityRule(card);
          quantity.value = String(this.normalizeQuantity(toNumber(quantity.value, rule.min) - rule.step, rule));
          this.syncQuantity(card);
          this.clearInventoryError(card);
          this.dispatchChange();
        });
        card.querySelector('[data-lf-addon-quantity-increase]')?.addEventListener('click', () => {
          const rule = this.getQuantityRule(card);
          const requestedQuantity = toNumber(quantity.value, rule.min) + rule.step;
          if (requestedQuantity > rule.max) {
            this.showInventoryError(card, rule.max);
            return;
          }
          quantity.value = String(this.normalizeQuantity(requestedQuantity, rule));
          this.syncQuantity(card);
          this.clearInventoryError(card);
          this.dispatchChange();
        });
        quantity?.addEventListener('change', () => {
          const rule = this.getQuantityRule(card);
          const requestedQuantity = toNumber(quantity.value, rule.min);
          this.syncQuantity(card);
          if (requestedQuantity > rule.max) this.showInventoryError(card, rule.max);
          else this.clearInventoryError(card);
          this.dispatchChange();
        });
        this.syncQuantity(card);
      });
      this.dispatchChange();
    }

    getQuantityRule(card) {
      const select = card.querySelector('[data-lf-addon-variant]');
      const source = select?.selectedOptions[0] || card.querySelector('[data-lf-addon-checkbox]');
      const min = Math.max(1, toNumber(source?.dataset.minQuantity, 1));
      const max = Math.max(0, toNumber(source?.dataset.maxQuantity, 0));
      const step = Math.max(1, toNumber(source?.dataset.step, 1));
      return { min, max, step };
    }

    normalizeQuantity(value, rule) {
      if (rule.max < rule.min) return 0;
      const bounded = Math.max(rule.min, Math.min(rule.max, toNumber(value, rule.min)));
      return Math.min(rule.max, rule.min + (Math.floor((bounded - rule.min) / rule.step) * rule.step));
    }

    getInventoryErrorMessage(quantity) {
      const template = this.dataset.inventoryErrorTemplate || 'Sie können nur [quantity] Stück dieses Artikels in den Warenkorb legen.';
      return template.replace('[quantity]', String(quantity));
    }

    showInventoryError(card, quantity, message = '') {
      const error = card?.querySelector('[data-lf-addon-inventory-error]');
      const text = error?.querySelector('[data-lf-addon-inventory-error-text]');
      if (!error || !text) return;
      text.textContent = message || this.getInventoryErrorMessage(quantity);
      error.hidden = false;
    }

    clearInventoryError(card) {
      const error = card?.querySelector('[data-lf-addon-inventory-error]');
      const text = error?.querySelector('[data-lf-addon-inventory-error-text]');
      if (text) text.textContent = '';
      if (error) error.hidden = true;
    }

    showInventoryErrorForVariant(variantId, quantity, message = '') {
      const card = Array.from(this.querySelectorAll('[data-lf-addon-card]')).find((candidate) => {
        const checkbox = candidate.querySelector('[data-lf-addon-checkbox]');
        return checkbox?.checked && String(checkbox.value) === String(variantId);
      });
      if (card) this.showInventoryError(card, quantity, message);
    }

    showServerInventoryError(message) {
      const selectedCards = Array.from(this.querySelectorAll('[data-lf-addon-card]')).filter((card) => {
        const checkbox = card.querySelector('[data-lf-addon-checkbox]');
        return checkbox?.checked;
      });
      const normalizedMessage = String(message).toLowerCase();
      const matchingCard = selectedCards.find((card) => {
        const title = card.querySelector('.lf-pdp__addon-title')?.textContent.trim().toLowerCase();
        return title && normalizedMessage.includes(title);
      });
      const card = matchingCard || (selectedCards.length === 1 ? selectedCards[0] : null);
      if (card) this.showInventoryError(card, this.getQuantityRule(card).max, message);
    }

    syncQuantity(card) {
      const checkbox = card.querySelector('[data-lf-addon-checkbox]');
      const quantity = card.querySelector('[data-lf-addon-quantity]');
      const decrease = card.querySelector('[data-lf-addon-quantity-decrease]');
      const increase = card.querySelector('[data-lf-addon-quantity-increase]');
      const rule = this.getQuantityRule(card);
      const available = rule.max >= rule.min;

      if (quantity) {
        quantity.min = String(rule.min);
        quantity.max = String(rule.max);
        quantity.step = String(rule.step);
        quantity.value = String(this.normalizeQuantity(quantity.value, rule));
      }
      if (checkbox) {
        checkbox.disabled = !available;
        if (!available) checkbox.checked = false;
      }

      const value = toNumber(quantity?.value, rule.min);
      if (decrease) decrease.disabled = !available || value - rule.step < rule.min;
      if (increase) {
        const atMaximum = !available || value + rule.step > rule.max;
        increase.disabled = !available;
        increase.classList.toggle('is-disabled', atMaximum);
        increase.setAttribute('aria-disabled', String(atMaximum));
      }
      return { ...rule, available, value };
    }

    getItems() {
      return Array.from(this.querySelectorAll('[data-lf-addon-card]')).flatMap((card) => {
        const checkbox = card.querySelector('[data-lf-addon-checkbox]');
        if (!checkbox?.checked || checkbox.disabled) return [];
        const quantityRule = this.syncQuantity(card);
        if (!quantityRule.available) return [];
        const select = card.querySelector('[data-lf-addon-variant]');
        const option = select?.selectedOptions[0];
        const price = select ? toNumber(option?.dataset.price) : this.priceFromText(card.querySelector('[data-lf-addon-price]'));
        return [{
          id: toNumber(checkbox.value),
          quantity: quantityRule.value,
          maxQuantity: quantityRule.max,
          price,
          label: card.querySelector('.lf-pdp__addon-title')?.textContent.trim() || 'Häufig zusammen gekaufter Artikel'
        }];
      });
    }

    priceFromText(element) {
      const cents = element?.dataset.price;
      return toNumber(cents);
    }

    dispatchChange() {
      this.dispatchEvent(new CustomEvent(LF_EVENT_ADDONS, { bubbles: true, detail: { items: this.getItems() } }));
    }
  }

  class LFProductGifts extends HTMLElement {
    connectedCallback() {
      if (this.lfConnected) return;
      this.lfConnected = true;
      this.clearButton = this.querySelector('[data-lf-clear-gift]');
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-lf-gift-radio]')) return;
        if (this.clearButton) this.clearButton.hidden = false;
        this.setVerifiedState('pending');
        this.dispatchChange();
      });
      this.clearButton?.addEventListener('click', () => {
        this.clearSelection();
      });
    }

    getItem() {
      const radio = this.querySelector('[data-lf-gift-radio]:checked');
      if (!radio || radio.disabled || radio.dataset.available !== 'true' || toNumber(radio.dataset.maxQuantity) < 1) return null;
      return {
        id: toNumber(radio.value),
        quantity: 1,
        maxQuantity: toNumber(radio.dataset.maxQuantity),
        price: toNumber(radio.dataset.price),
        label: radio.closest('.lf-pdp__gift-card')?.querySelector('strong')?.textContent.trim() || 'Gratisgeschenk'
      };
    }

    setVerifiedState(state) {
      this.dataset.lfGiftVerification = state;
      this.querySelectorAll('[data-lf-gift-price-status]').forEach((status) => {
        status.textContent = state === 'failed' ? 'Nicht rabattiert' : state === 'verified' ? 'GRATIS' : 'GRATIS*';
      });
    }

    clearSelection(state = 'pending', dispatch = true) {
      this.querySelectorAll('[data-lf-gift-radio]').forEach((radio) => { radio.checked = false; });
      if (this.clearButton) this.clearButton.hidden = true;
      this.setVerifiedState(state);
      if (dispatch) this.dispatchChange();
    }

    dispatchChange() {
      this.dispatchEvent(new CustomEvent(LF_EVENT_GIFT, { bubbles: true, detail: { item: this.getItem() } }));
    }
  }

  class LFProductNav extends HTMLElement {
    connectedCallback() {
      if (this.lfConnected) return;
      this.lfConnected = true;
      this.buttons = Array.from(this.querySelectorAll('[data-lf-nav-target]'));
      this.targets = this.buttons
        .map((button) => document.getElementById(button.dataset.lfNavTarget))
        .filter(Boolean)
        .sort((first, second) => {
          if (first === second) return 0;
          return first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        });
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateActiveTarget();
        });
      };
      this.onResize = () => {
        this.bindVisibilityObserver();
        this.updateActiveTarget();
      };

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.navigateTo(button));
      });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.bindVisibilityObserver();
      this.updateActiveTarget();
    }

    getFixedOffset() {
      return Math.max(0, this.getBoundingClientRect().bottom) + 10;
    }

    navigateTo(button) {
      const target = document.getElementById(button.dataset.lfNavTarget);
      if (!target) return;

      if (target.matches('details')) target.open = true;
      this.setActiveTarget(target.id);

      if (this.navigationFrame) window.cancelAnimationFrame(this.navigationFrame);
      this.navigationFrame = window.requestAnimationFrame(() => {
        this.navigationFrame = null;
        this.scrollTargetIntoView(target);
      });
    }

    scrollTargetIntoView(target) {
      const anchor = target.matches('details') ? target.querySelector('summary') || target : target;
      if (this.isTargetAligned(anchor)) return;

      const top = anchor.getBoundingClientRect().top + window.scrollY - this.getFixedOffset();
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      window.scrollTo({ top: Math.max(0, top), behavior });
    }

    isTargetAligned(anchor) {
      return Math.abs(anchor.getBoundingClientRect().top - this.getFixedOffset()) <= 2;
    }

    setActiveTarget(targetId) {
      this.buttons.forEach((button) => {
        const selected = button.dataset.lfNavTarget === targetId;
        button.classList.toggle('is-active', selected);
        if (selected) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }

    bindVisibilityObserver() {
      this.visibilityObserver?.disconnect();
      const mobile = window.matchMedia('(max-width: 1023px)').matches;
      if (mobile) {
        this.classList.add('is-visible');
        return;
      }
      const media = this.closest('lf-product-purchase')?.querySelector('[data-lf-media-column]');
      if (!media || !('IntersectionObserver' in window)) {
        this.classList.add('is-visible');
        return;
      }
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0.05 });
      this.visibilityObserver.observe(media);
    }

    updateActiveTarget() {
      if (!this.targets.length) return;
      const marker = this.getFixedOffset() + 18;
      let active = this.targets[0];
      this.targets.forEach((target) => {
        if (target.getBoundingClientRect().top <= marker) active = target;
      });
      this.setActiveTarget(active.id);
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.navigationFrame) window.cancelAnimationFrame(this.navigationFrame);
    }
  }

  class LFStickyPurchase extends HTMLElement {
    connectedCallback() {
      if (this.lfConnected) return;
      this.lfConnected = true;
      this.onResize = () => this.bindObserver();
      this.onMobileScroll = () => {
        if (this.mobileScrollFrame) return;
        this.mobileScrollFrame = window.requestAnimationFrame(() => {
          this.mobileScrollFrame = null;
          this.updateMobileVisibility();
        });
      };
      window.addEventListener('resize', this.onResize, { passive: true });
      this.bindObserver();
    }

    bindObserver() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onMobileScroll);
      this.mobileTrigger = null;
      const purchase = this.closest('lf-product-purchase');
      if (window.matchMedia('(max-width: 1023px)').matches) {
        this.mobileTrigger = purchase?.querySelector('[data-lf-price-block]')
          || purchase?.querySelector('[data-lf-buy-box]')
          || purchase?.querySelector('[data-lf-media-column]');
        if (!this.mobileTrigger) {
          this.hidden = false;
          return;
        }
        this.mobileTriggerTop = Math.max(0, Math.ceil(purchase?.querySelector('[data-lf-product-nav]')?.getBoundingClientRect().bottom || 0));
        this.updateMobileVisibility();
        if (!('IntersectionObserver' in window)) {
          window.addEventListener('scroll', this.onMobileScroll, { passive: true });
          return;
        }
        this.observer = new IntersectionObserver(([entry]) => {
          this.hidden = entry.isIntersecting || entry.boundingClientRect.top >= this.mobileTriggerTop;
        }, {
          rootMargin: `-${this.mobileTriggerTop}px 0px 0px 0px`,
          threshold: 0
        });
        this.observer.observe(this.mobileTrigger);
        return;
      }
      const media = purchase?.querySelector('[data-lf-media-column]');
      if (!media || !('IntersectionObserver' in window)) {
        this.hidden = false;
        return;
      }
      this.observer = new IntersectionObserver(([entry]) => {
        this.hidden = entry.isIntersecting;
      }, { threshold: 0.05 });
      this.observer.observe(media);
    }

    updateMobileVisibility() {
      if (!this.mobileTrigger) return;
      this.hidden = this.mobileTrigger.getBoundingClientRect().bottom > this.mobileTriggerTop;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onMobileScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.mobileScrollFrame) window.cancelAnimationFrame(this.mobileScrollFrame);
    }
  }

  class LFProductPurchase extends HTMLElement {
    connectedCallback() {
      if (this.lfConnected) return;
      this.lfConnected = true;
      const data = parseJson(this.querySelector('[data-lf-product-json]'), { variants: [] });
      this.variants = Array.isArray(data.variants) ? data.variants : [];
      this.strictInventoryMode = this.dataset.lfStrictInventoryMode === 'true';
      this.strictInStockVariantIds = new Set(String(this.dataset.lfStrictInStockVariantIds || '').split('|').filter(Boolean));
      const selectedVariant = this.variants.find((variant) => String(variant.id) === String(data.selectedVariantId));
      const firstPurchasableVariant = this.variants.find((variant) => this.isVariantPurchasable(variant));
      this.currentVariant = (this.dataset.hideUnavailableOptions === 'true' && !this.isVariantPurchasable(selectedVariant)
        ? firstPurchasableVariant
        : selectedVariant)
        || firstPurchasableVariant
        || this.variants[0];
      this.addonItems = [];
      this.giftItem = null;
      this.giftVerificationFailed = false;
      this.pendingPromotions = new Map();
      this.busy = false;
      this.currency = this.dataset.currency || 'USD';
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency
      });

      this.querySelectorAll('[data-lf-action]').forEach((button) => {
        const label = button.querySelector('span');
        button.dataset.lfOriginalLabel = label?.textContent.trim() || '';
      });
      this.addEventListener(LF_EVENT_VARIANT, (event) => this.setVariant(event.detail.variant, true));
      this.addEventListener(LF_EVENT_ADDONS, (event) => {
        this.addonItems = event.detail.items || [];
        this.updateTotals();
      });
      this.addEventListener(LF_EVENT_GIFT, (event) => {
        this.giftItem = event.detail.item || null;
        this.giftVerificationFailed = false;
        this.updateTotals();
      });
      this.addEventListener('click', (event) => {
        const action = event.target.closest('[data-lf-action]');
        if (action && this.contains(action)) this.submitPurchase(action.dataset.lfAction, action);

        const copyButton = event.target.closest('[data-lf-copy-code]');
        if (copyButton && this.contains(copyButton)) {
          this.copyPromotion(copyButton.closest('[data-lf-promo]'));
          return;
        }

        const promoButton = event.target.closest('[data-lf-apply-code]');
        if (promoButton && this.contains(promoButton)) {
          this.togglePromotion(promoButton.closest('[data-lf-promo]'));
          return;
        }

        const promotion = event.target.closest('[data-lf-promo]');
        const nestedControl = event.target.closest('a, input, select, textarea');
        if (promotion && this.contains(promotion) && !nestedControl) this.togglePromotion(promotion);
      });

      this.querySelector('lf-variant-picker')?.initialize(this.variants, this.currentVariant);
      this.giftItem = this.querySelector('lf-product-gifts')?.getItem() || null;
      this.addonItems = this.querySelector('lf-product-addons')?.getItems() || [];
      this.setVariant(this.currentVariant, false);
      this.startCountdowns();
    }

    disconnectedCallback() {
      if (this.countdownTimer) window.clearInterval(this.countdownTimer);
    }

    formatMoney(cents) {
      return this.formatter.format(toNumber(cents) / 100);
    }

    isVariantPurchasable(variant) {
      if (!variant?.available) return false;
      return !this.strictInventoryMode || this.strictInStockVariantIds.has(String(variant.id));
    }

    setVariant(variant, updateUrl) {
      if (!variant) return;
      this.currentVariant = variant;
      const idInput = this.querySelector('[data-lf-variant-id]');
      if (idInput) idInput.value = variant.id;
      this.querySelectorAll('[data-lf-installment-amount]').forEach((amount) => {
        amount.textContent = this.formatMoney(Math.round(toNumber(variant.price) / 4));
      });
      this.querySelector('lf-variant-picker')?.syncInputs(variant.options || []);
      this.querySelector('lf-variant-picker')?.updateOptionAvailability();
      const mediaId = variant.featured_media?.id || variant.featured_media_id;
      const gallery = this.querySelector('lf-product-gallery');
      if (gallery?.filterForVariant) gallery.filterForVariant(variant, mediaId);
      else if (mediaId) gallery?.activateMedia(mediaId);
      this.updatePromotions();
      this.updateAvailability();
      this.updateTotals();
      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    updateAvailability() {
      const available = this.isVariantPurchasable(this.currentVariant);
      this.querySelectorAll('[data-lf-action]').forEach((button) => {
        button.disabled = !available || this.busy;
        button.setAttribute('aria-disabled', String(!available || this.busy));
        const label = button.querySelector('span');
        if (label && !this.busy) label.textContent = available ? button.dataset.lfOriginalLabel : 'Ausverkauft';
      });
      this.querySelectorAll('[data-lf-sticky-status]').forEach((status) => {
        status.textContent = available ? (this.currentVariant.title || 'Verfügbar') : 'Ausverkauft';
      });
    }

    promotionEligible(promotion) {
      if (this.promotionExpired(promotion)) return false;
      const eligible = String(promotion.dataset.eligibleVariants || '').split(',').map((id) => id.trim()).filter(Boolean);
      const restricted = promotion.dataset.eligibilityRestricted === 'true';
      return restricted ? eligible.includes(String(this.currentVariant?.id)) : true;
    }

    promotionExpired(promotion) {
      if (promotion.dataset.lfExpired === 'true') return true;
      const endAt = promotion.dataset.endAt?.trim();
      if (!endAt) return false;
      const deadline = new Date(endAt).getTime();
      return Number.isFinite(deadline) && deadline <= Date.now();
    }

    promotionKey(promotion) {
      return promotion?.dataset.promotionKey || promotion?.dataset.code || '';
    }

    isPromotionQueued(promotion) {
      const key = this.promotionKey(promotion);
      return Boolean(key && this.pendingPromotions.has(key));
    }

    promotionAllowsCombination(promotion) {
      return promotion?.dataset.allowCombination === 'true';
    }

    promotionByKey(key) {
      return Array.from(this.querySelectorAll('[data-lf-promo]')).find((promotion) => {
        return this.promotionKey(promotion) === key;
      });
    }

    queuePromotion(promotion) {
      const key = this.promotionKey(promotion);
      const code = promotion?.dataset.code;
      if (!key || !code) return false;
      const normalizedCode = code.toLowerCase();
      const allowsCombination = this.promotionAllowsCombination(promotion);
      this.pendingPromotions.forEach((pendingCode, pendingKey) => {
        if (pendingKey === key) return;
        const pendingPromotion = this.promotionByKey(pendingKey);
        const duplicateCode = String(pendingCode).toLowerCase() === normalizedCode;
        const incompatible = !allowsCombination || !this.promotionAllowsCombination(pendingPromotion);
        if (duplicateCode || incompatible) {
          this.pendingPromotions.delete(pendingKey);
        }
      });
      this.pendingPromotions.set(key, code);
      return true;
    }

    removePromotion(promotion) {
      const key = this.promotionKey(promotion);
      return key ? this.pendingPromotions.delete(key) : false;
    }

    pendingPromotionCodes() {
      const codes = new Map();
      this.pendingPromotions.forEach((code) => {
        const normalizedCode = String(code).trim().toLowerCase();
        if (normalizedCode && !codes.has(normalizedCode)) codes.set(normalizedCode, code);
      });
      return Array.from(codes.values());
    }

    updatePromotions() {
      this.querySelectorAll('[data-lf-promo]').forEach((promotion) => {
        const eligible = this.promotionEligible(promotion);
        const code = promotion.dataset.code;
        const button = promotion.querySelector('[data-lf-apply-code]');
        const copyButton = promotion.querySelector('[data-lf-copy-code]');
        const status = promotion.querySelector('[data-lf-promo-status]');
        const copyLabel = promotion.querySelector('[data-lf-copy-label]');
        const applied = Boolean(code && this.isPromotionQueued(promotion));
        promotion.hidden = !eligible;
        promotion.classList.toggle('is-disabled', !eligible);
        promotion.classList.toggle('is-applied', eligible && applied);
        if (button) button.disabled = !eligible;
        button?.setAttribute('aria-pressed', String(eligible && applied));
        button?.setAttribute('aria-label', `${promotion.dataset.offerLabel || 'Angebot'} ${eligible && applied ? 'entfernen' : 'anwenden'}`);
        if (copyButton) {
          copyButton.disabled = !eligible;
          copyButton.setAttribute('aria-label', `${eligible && applied ? 'Rabattcode kopiert' : 'Rabattcode kopieren'} ${code || ''}`.trim());
        }
        if (copyLabel) copyLabel.textContent = eligible && applied ? 'Kopiert' : 'Kopieren';
        if (!eligible) this.removePromotion(promotion);
        if (!status) return;
        status.classList.remove('is-error', 'is-applied');
        if (!eligible) {
          status.textContent = 'Dieses Angebot ist für die ausgewählte Variante nicht verfügbar.';
        } else if (code && applied) {
          status.textContent = `${code} ist vorgemerkt. Shopify bestätigt den Code im Warenkorb und beim Checkout.`;
          status.classList.add('is-applied');
        } else {
          status.textContent = 'Wählen Sie dieses Angebot aus, um die Ersparnis als Vorschau anzuzeigen. Shopify bestätigt den Rabatt im Warenkorb und beim Checkout.';
        }
      });
      this.querySelectorAll('.lf-pdp__promotions').forEach((container) => {
        const hasVisiblePromotion = Array.from(container.querySelectorAll('[data-lf-promo]')).some((promotion) => !promotion.hidden);
        container.hidden = !hasVisiblePromotion;
      });
    }

    togglePromotion(promotion) {
      if (!promotion || !this.promotionEligible(promotion)) return;
      const code = promotion.dataset.code;
      if (!code) return;
      const wasQueued = this.isPromotionQueued(promotion);
      if (!wasQueued) {
        this.copyPromotion(promotion);
        return;
      }
      this.removePromotion(promotion);
      this.updatePromotions();
      this.updateTotals();
    }

    async copyPromotion(promotion) {
      if (!promotion || !this.promotionEligible(promotion)) return;
      const code = promotion.dataset.code;
      const copyButton = promotion.querySelector('[data-lf-copy-code]');
      if (!code || !copyButton) return;

      await this.copyCode(code);
      this.queuePromotion(promotion);
      this.updatePromotions();
      this.updateTotals();
    }

    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const input = document.createElement('textarea');
        input.value = code;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
    }

    estimatedDiscount() {
      const price = toNumber(this.currentVariant?.price);
      let discount = 0;
      this.querySelectorAll('[data-lf-promo]').forEach((promotion) => {
        const code = promotion.dataset.code;
        if (!code || !this.isPromotionQueued(promotion) || !this.promotionEligible(promotion)) return;
        const value = Math.max(0, toNumber(promotion.dataset.discountValue));
        discount += promotion.dataset.discountType === 'amount' ? Math.round(value * 100) : Math.round(price * value / 100);
      });
      return Math.min(price, discount);
    }

    updatePriceSummary() {
      const price = toNumber(this.currentVariant?.price);
      const compareAtPrice = toNumber(this.currentVariant?.compare_at_price);
      const promotionSaving = this.estimatedDiscount();
      const effectivePrice = Math.max(0, price - promotionSaving);
      const nativeSaving = Math.max(0, compareAtPrice - price);
      const shownComparePrice = promotionSaving > 0 ? price : compareAtPrice;
      const shownSaving = promotionSaving > 0 ? promotionSaving : nativeSaving;

      this.querySelectorAll('[data-lf-price]').forEach((element) => {
        element.textContent = this.formatMoney(promotionSaving > 0 ? effectivePrice : price);
      });
      this.querySelectorAll('[data-lf-compare-price]').forEach((element) => {
        element.textContent = this.formatMoney(shownComparePrice);
        element.classList.toggle('is-hidden', shownSaving <= 0);
      });
      this.querySelectorAll('[data-lf-saving]').forEach((element) => {
        element.textContent = `Sie sparen ${this.formatMoney(shownSaving)}`;
        element.classList.toggle('is-hidden', shownSaving <= 0);
      });
      this.querySelectorAll('[data-lf-price-block]').forEach((element) => {
        element.classList.toggle('is-promotion-applied', promotionSaving > 0);
      });
    }

    updateTotals() {
      const addonTotal = this.addonItems.reduce((total, item) => total + (item.price * item.quantity), 0);
      const failedGiftPrice = this.giftVerificationFailed && this.giftItem ? this.giftItem.price : 0;
      const baseTotal = toNumber(this.currentVariant?.price) + addonTotal + failedGiftPrice;
      const promotionSaving = this.estimatedDiscount();
      const total = Math.max(0, baseTotal - promotionSaving);
      this.updatePriceSummary();
      this.querySelectorAll('[data-lf-total], [data-lf-sticky-total]').forEach((element) => {
        element.textContent = this.formatMoney(total);
      });
      this.querySelectorAll('[data-lf-total-compare], [data-lf-sticky-compare]').forEach((element) => {
        element.textContent = this.formatMoney(baseTotal);
        element.classList.toggle('is-hidden', promotionSaving <= 0);
      });
      this.querySelectorAll('[data-lf-total-saving], [data-lf-sticky-saving]').forEach((element) => {
        element.textContent = `Sie sparen ${this.formatMoney(promotionSaving)}`;
        element.classList.toggle('is-hidden', promotionSaving <= 0);
      });
    }

    startCountdowns() {
      const promotions = Array.from(this.querySelectorAll('[data-lf-promo]')).filter((promotion) => {
        return promotion.querySelector('[data-lf-countdown]') && Boolean(promotion.dataset.endAt?.trim());
      });

      if (!promotions.length) return;

      const update = () => {
        promotions.forEach((promotion) => {
          if (promotion.dataset.lfExpired === 'true') return;
          const countdown = promotion.querySelector('[data-lf-countdown]');
          if (!countdown) return;
          if (!promotion.dataset.lfDeadline) {
            const deadline = new Date(promotion.dataset.endAt).getTime();
            if (!Number.isFinite(deadline)) {
              countdown.closest('.lf-pdp__promo-countdown')?.setAttribute('hidden', '');
              promotion.querySelector('.lf-pdp__promo-header')?.classList.add('lf-pdp__promo-header--without-countdown');
              return;
            }
            promotion.dataset.lfDeadline = String(deadline);
          }
          const distance = toNumber(promotion.dataset.lfDeadline) - Date.now();
          if (!Number.isFinite(distance)) {
            countdown.textContent = '';
            return;
          }
          if (distance <= 0) {
            promotion.dataset.lfExpired = 'true';
            this.removePromotion(promotion);
            this.updatePromotions();
            this.updateTotals();
            return;
          }
          const days = Math.floor(distance / 86400000);
          const hours = Math.floor((distance % 86400000) / 3600000);
          const minutes = Math.floor((distance % 3600000) / 60000);
          const seconds = Math.floor((distance % 60000) / 1000);
          const pad = (value) => String(value).padStart(2, '0');
          countdown.textContent = `${pad(days)}T : ${pad(hours)}Std : ${pad(minutes)}Min : ${pad(seconds)}Sek`;
        });
      };
      update();
      this.countdownTimer = window.setInterval(update, 1000);
    }

    buildPurchaseItems() {
      // Roles are only needed while this page validates the selection. Do not
      // serialize them as Shopify properties, because those persist on orders.
      const items = [{ id: this.currentVariant.id, quantity: 1, role: 'main' }];
      this.addonItems.forEach((item) => {
        items.push({ id: item.id, quantity: item.quantity, role: 'addon' });
      });
      if (this.giftItem) items.push({ id: this.giftItem.id, quantity: 1, role: 'gift' });
      return items;
    }

    validatePurchaseRoleConflicts(items) {
      const rolesByVariant = new Map();
      (items || []).forEach((item) => {
        const id = String(item?.id || '');
        if (!id || !item?.role) return;
        if (!rolesByVariant.has(id)) rolesByVariant.set(id, new Set());
        rolesByVariant.get(id).add(item.role);
      });
      const conflict = Array.from(rolesByVariant.values()).find((roles) => roles.size > 1);
      if (!conflict) return { valid: true };
      return {
        valid: false,
        message: 'Dieselbe Produktoption kann nicht in mehreren Rollen verwendet werden (Hauptartikel, Zusatzartikel oder Gratisgeschenk). Bitte prüfen Sie Ihre Auswahl.'
      };
    }

    mergePurchaseItems(items) {
      const quantitiesByVariant = new Map();
      (items || []).forEach((item) => {
        const id = String(item?.id || '');
        if (!id) return;
        const quantity = Math.max(1, Math.floor(toNumber(item.quantity, 1)));
        const existing = quantitiesByVariant.get(id);
        quantitiesByVariant.set(id, {
          id: existing?.id ?? item.id,
          quantity: (existing?.quantity || 0) + quantity
        });
      });
      return Array.from(quantitiesByVariant.values());
    }

    buildCartItems(items) {
      return this.mergePurchaseItems(items);
    }

    buildCheckoutLines(items) {
      return this.mergePurchaseItems(items).map((item) => ({
        merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
        quantity: item.quantity
      }));
    }

    checkoutVariantId(line) {
      return String(line?.merchandise?.id || '').split('/').pop();
    }

    cartItemVariantId(item) {
      return String(item?.variant_id || item?.id || '');
    }

    cartItemsForVariant(cart, expectedItem) {
      if (!expectedItem) return [];
      return (cart?.items || []).filter((item) => {
        return this.cartItemVariantId(item) === String(expectedItem.id);
      });
    }

    isCartItemFree(item) {
      if (toNumber(item?.quantity) < 1) return false;
      return toNumber(item?.final_price, -1) === 0 || toNumber(item?.final_line_price, -1) === 0;
    }

    cartItemSignature(item) {
      const rawProperties = item?.properties && typeof item.properties === 'object' ? item.properties : {};
      const properties = Object.entries(rawProperties)
        .filter(([, value]) => value !== null && value !== undefined)
        .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
        .map(([key, value]) => [key, String(value)]);
      const sellingPlanId = item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan || '';
      const parentKey = item?.parent_relationship?.parent?.key || item?.parent_relationship?.parent_key || '';
      return JSON.stringify({ properties, sellingPlanId: String(sellingPlanId), parentKey: String(parentKey) });
    }

    cartVariantStats(cart, expectedItem) {
      const items = this.cartItemsForVariant(cart, expectedItem);
      return {
        items,
        quantity: items.reduce((total, item) => total + Math.max(0, Math.floor(toNumber(item.quantity))), 0),
        freeQuantity: items.reduce((total, item) => {
          return total + (this.isCartItemFree(item) ? Math.max(0, Math.floor(toNumber(item.quantity))) : 0);
        }, 0)
      };
    }

    quantitiesByVariant(items, getVariantId) {
      const quantities = new Map();
      (items || []).forEach((item) => {
        const id = String(getVariantId(item) || '');
        if (!id) return;
        quantities.set(id, (quantities.get(id) || 0) + Math.max(1, Math.floor(toNumber(item.quantity, 1))));
      });
      return quantities;
    }

    findPurchaseItem(items, role) {
      return (items || []).find((item) => item.role === role) || null;
    }

    findCartItem(cart, expectedItem, preferredKey = '') {
      const candidates = this.cartItemsForVariant(cart, expectedItem);
      if (!candidates.length) return null;
      if (preferredKey) {
        const matchedKey = candidates.find((item) => item.key === preferredKey);
        if (matchedKey) return matchedKey;
      }
      const expectedQuantity = Math.max(1, Math.floor(toNumber(expectedItem.quantity, 1)));
      return candidates.find((item) => toNumber(item.quantity) === expectedQuantity)
        || candidates.find((item) => toNumber(item.quantity) >= expectedQuantity)
        || candidates[0];
    }

    buildGiftRollbackUpdates(cart, baselineCart, giftItem, preferredKey = '') {
      const current = this.cartVariantStats(cart, giftItem);
      const baseline = this.cartVariantStats(baselineCart, giftItem);
      const expectedQuantity = Math.max(1, Math.floor(toNumber(giftItem?.quantity, 1)));
      const expectedRemoval = Math.min(expectedQuantity, Math.max(0, current.quantity - baseline.quantity));
      if (!expectedRemoval) return { updates: {}, expectedRemoval: 0, removedQuantity: 0 };

      const baselineBySignature = new Map();
      baseline.items.forEach((item) => {
        const signature = this.cartItemSignature(item);
        baselineBySignature.set(signature, (baselineBySignature.get(signature) || 0) + Math.max(0, Math.floor(toNumber(item.quantity))));
      });

      const currentBySignature = new Map();
      current.items.forEach((item) => {
        const signature = this.cartItemSignature(item);
        currentBySignature.set(signature, (currentBySignature.get(signature) || 0) + Math.max(0, Math.floor(toNumber(item.quantity))));
      });

      const excessBySignature = new Map();
      currentBySignature.forEach((quantity, signature) => {
        excessBySignature.set(signature, Math.max(0, quantity - (baselineBySignature.get(signature) || 0)));
      });

      const candidates = current.items
        .filter((item) => item.key)
        .map((item) => ({
          item,
          quantity: Math.max(0, Math.floor(toNumber(item.quantity))),
          signature: this.cartItemSignature(item)
        }))
        .sort((first, second) => {
          const score = (candidate) => {
            const signatureExcess = excessBySignature.get(candidate.signature) || 0;
            return (signatureExcess > 0 ? 100 : 0)
              + (!this.isCartItemFree(candidate.item) ? 20 : 0)
              + (candidate.item.key === preferredKey ? 10 : 0);
          };
          return score(second) - score(first);
        });

      const updates = {};
      let remaining = expectedRemoval;
      candidates.forEach((candidate) => {
        if (!remaining) return;
        const signatureExcess = excessBySignature.get(candidate.signature) || 0;
        if (!signatureExcess) return;
        const reduction = Math.min(candidate.quantity, signatureExcess, remaining);
        if (!reduction) return;
        updates[candidate.item.key] = candidate.quantity - reduction;
        excessBySignature.set(candidate.signature, signatureExcess - reduction);
        remaining -= reduction;
      });

      return {
        updates,
        expectedRemoval,
        removedQuantity: expectedRemoval - remaining
      };
    }

    findCheckoutGiftLine(lines, expectedGift) {
      if (!expectedGift) return null;
      const expectedQuantity = Math.max(1, Math.floor(toNumber(expectedGift.quantity, 1)));
      const candidates = lines.filter((line) => this.checkoutVariantId(line) === String(expectedGift.id));
      return candidates.find((line) => (
        toNumber(line.quantity) >= expectedQuantity
        && toNumber(line.cost?.totalAmount?.amount, -1) === 0
      )) || candidates.find((line) => toNumber(line.quantity) >= expectedQuantity) || candidates[0] || null;
    }

    validateIsolatedCheckoutCart(cart, expectedItems, requestedCodes) {
      const lines = Array.isArray(cart?.lines?.nodes) ? cart.lines.nodes.filter(Boolean) : [];
      const expectedQuantities = this.quantitiesByVariant(expectedItems, (item) => item.id);
      const actualQuantities = this.quantitiesByVariant(lines, (line) => this.checkoutVariantId(line));

      for (const [variantId, quantity] of expectedQuantities) {
        if (actualQuantities.get(variantId) !== quantity) {
          throw new Error('Mindestens ein ausgewählter Artikel ist in der gewünschten Menge nicht verfügbar. Bitte prüfen Sie Ihre Auswahl.');
        }
      }

      const returnedCodes = Array.isArray(cart?.discountCodes) ? cart.discountCodes : [];
      const unavailableCodes = requestedCodes.filter((code) => {
        const returnedCode = returnedCodes.find((item) => String(item.code).toLowerCase() === code.toLowerCase());
        return !returnedCode?.applicable;
      });
      if (unavailableCodes.length) {
        throw new Error(`${unavailableCodes.join(', ')} kann auf diese Auswahl nicht angewendet werden. Der Checkout wurde nicht geöffnet.`);
      }

      const expectedGift = this.findPurchaseItem(expectedItems, 'gift');
      if (expectedGift) {
        const giftLine = this.findCheckoutGiftLine(lines, expectedGift);
        if (!giftLine || toNumber(giftLine.cost?.totalAmount?.amount, -1) !== 0) {
          if (String(this.giftItem?.id) === String(expectedGift.id)) {
            this.giftVerificationFailed = true;
            this.querySelector('lf-product-gifts')?.setVerifiedState('failed');
            this.updateTotals();
          }
          throw new Error('Das ausgewählte Geschenk war bei diesem Checkout nicht kostenlos. Der Checkout wurde nicht geöffnet.');
        }
        if (String(this.giftItem?.id) === String(expectedGift.id)) {
          this.querySelector('lf-product-gifts')?.setVerifiedState('verified');
        }
      }

      if (!expectedGift || String(this.giftItem?.id) === String(expectedGift.id)) {
        this.giftVerificationFailed = false;
        this.updateTotals();
      }
    }

    async createIsolatedCheckout(items) {
      const codes = this.pendingPromotionCodes();
      const input = {
        lines: this.buildCheckoutLines(items)
      };
      if (codes.length) input.discountCodes = codes;
      const countryCode = String(this.dataset.countryCode || '').trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(countryCode)) input.buyerIdentity = { countryCode };

      const query = `
        mutation LFCreateBuyNowCart($input: CartInput) {
          cartCreate(input: $input) {
            cart {
              checkoutUrl
              discountCodes {
                code
                applicable
              }
              lines(first: 250) {
                nodes {
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                    }
                  }
                  cost {
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
            warnings {
              message
            }
          }
        }
      `;
      const response = await fetch(this.dataset.storefrontApiUrl, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ query, variables: { input } })
      });
      const data = await response.json().catch(() => ({}));
      const graphError = Array.isArray(data.errors) ? data.errors[0]?.message : '';
      if (!response.ok || graphError) {
        throw new Error(graphError || `Checkout-Anfrage fehlgeschlagen (${response.status}).`);
      }

      const result = data.data?.cartCreate;
      const userError = result?.userErrors?.[0]?.message;
      if (userError) throw new Error(userError);
      if (!result?.cart?.checkoutUrl) throw new Error('Shopify konnte für diese Auswahl keinen Checkout erstellen.');
      if (result.warnings?.length) console.warn('Shopify adjusted the Buy Now cart.', result.warnings);

      this.validateIsolatedCheckoutCart(result.cart, items, codes);
      return result.cart.checkoutUrl;
    }

    validateSecondaryInventory() {
      const addonComponent = this.querySelector('lf-product-addons');
      const giftComponent = this.querySelector('lf-product-gifts');
      this.addonItems = addonComponent?.getItems() || [];
      this.giftItem = giftComponent?.getItem() || null;

      const quantitiesByVariant = new Map();
      const limitsByVariant = new Map();
      const secondaryItems = [...this.addonItems, ...(this.giftItem ? [this.giftItem] : [])];

      secondaryItems.forEach((item) => {
        const id = String(item.id);
        quantitiesByVariant.set(id, (quantitiesByVariant.get(id) || 0) + toNumber(item.quantity));
        limitsByVariant.set(id, Math.min(limitsByVariant.get(id) ?? Infinity, toNumber(item.maxQuantity)));
      });

      for (const [id, requestedQuantity] of quantitiesByVariant) {
        const maxQuantity = limitsByVariant.get(id);
        if (requestedQuantity > maxQuantity) {
          const message = addonComponent?.getInventoryErrorMessage(maxQuantity)
            || `Sie können nur ${maxQuantity} Stück dieses Artikels in den Warenkorb legen.`;
          addonComponent?.showInventoryErrorForVariant(id, maxQuantity, message);
          return {
            valid: false,
            message
          };
        }
      }
      return { valid: true };
    }

    isInventoryError(message) {
      return /inventory|in stock|out of stock|sold out|only add|add more|availab|maximum quantity|bestand|auf lager|nicht verfügbar|ausverkauft|nur .*hinzufügen|nur .*warenkorb|höchstmenge|maximale menge/i.test(String(message));
    }

    setBusy(busy, activeButton = null) {
      this.busy = busy;
      this.querySelectorAll('[data-lf-action]').forEach((button) => {
        const isActive = busy && button === activeButton;
        button.classList.toggle('is-loading', isActive);
        if (isActive) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');

        const label = button.querySelector('span');
        if (label && isActive) {
          label.textContent = button.dataset.lfAction === 'buy' ? 'Weiter zum Checkout...' : 'Wird hinzugefügt...';
        }
      });
      this.setAttribute('aria-busy', String(busy));
      this.updateAvailability();
    }

    setStatus(message, type = '') {
      this.querySelectorAll('[data-lf-status]').forEach((status) => {
        status.textContent = message;
        status.classList.remove('is-success', 'is-error', 'is-warning');
        if (type) status.classList.add(`is-${type}`);
      });
    }

    getBasePath() {
      const root = this.dataset.rootUrl || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    getSections() {
      return ['main-cart', 'cart-drawer', 'cart-bubble'];
    }

    async postJson(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) throw new Error(data.description || data.message || `Warenkorbanfrage fehlgeschlagen (${response.status}).`);
      return data;
    }

    wait(duration) {
      return new Promise((resolve) => window.setTimeout(resolve, duration));
    }

    async getMbcCartUpdater(giftItem = this.giftItem, timeout = 3000) {
      if (!giftItem) return null;
      const startedAt = Date.now();
      const appIsExpected = () => Boolean(
        window.MAKEBECOOL_BUNDLES
        || document.querySelector('mbc-bundles-widget, script[src*="mbcbundlesapp"], script[src*="mbc-bundles"]')
      );

      while (Date.now() - startedAt < timeout) {
        const mbc = window.MAKEBECOOL_BUNDLES;
        if (typeof mbc?.updateCart === 'function') return mbc.updateCart.bind(mbc);
        if (!appIsExpected()) return null;
        await this.wait(50);
      }
      return null;
    }

    async syncMbcCart(giftItem = this.giftItem) {
      const updateCart = await this.getMbcCartUpdater(giftItem);
      if (!updateCart) return false;
      try {
        await updateCart();
        return true;
      } catch (error) {
        console.error('MBC could not refresh the gift discount.', error);
        return false;
      }
    }

    async refreshCartSections(sections) {
      try {
        return await this.postJson(`${this.getBasePath()}cart/update.js`, {
          sections,
          sections_url: window.location.pathname
        });
      } catch (error) {
        console.error('The cart sections could not be refreshed after the MBC update.', error);
        return null;
      }
    }

    async fetchCart(giftItem = null, baselineCart = null, attempts = 1) {
      let lastError;
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
          const response = await fetch(`${this.getBasePath()}cart.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Der Warenkorb konnte nicht überprüft werden.');
          const cart = await response.json();
          if (!giftItem || this.verifyGift(cart, giftItem, baselineCart) || attempt === attempts - 1) return cart;
        } catch (error) {
          lastError = error;
          if (attempt === attempts - 1) throw error;
        }
        await this.wait(150 * (attempt + 1));
      }
      throw lastError || new Error('Der Warenkorb konnte nicht überprüft werden.');
    }

    async restoreGiftQuantity(cart, baselineCart, giftItem, preferredKey, sections) {
      const rollback = this.buildGiftRollbackUpdates(cart, baselineCart, giftItem, preferredKey);
      if (!rollback.expectedRemoval) return { cart, restored: true };
      if (rollback.removedQuantity !== rollback.expectedRemoval) return { cart, restored: false };
      const correctedCart = await this.postJson(`${this.getBasePath()}cart/update.js`, {
        updates: rollback.updates,
        sections,
        sections_url: window.location.pathname
      });
      return { cart: correctedCart, restored: true };
    }

    async submitPurchase(mode, trigger = null) {
      if (this.busy || !this.isVariantPurchasable(this.currentVariant)) return;
      const inventoryValidation = this.validateSecondaryInventory();
      if (!inventoryValidation.valid) {
        this.updateTotals();
        this.setStatus(inventoryValidation.message, 'error');
        return;
      }
      const purchaseItems = this.buildPurchaseItems();
      const roleValidation = this.validatePurchaseRoleConflicts(purchaseItems);
      if (!roleValidation.valid) {
        this.setStatus(roleValidation.message, 'error');
        return;
      }
      const selectedGift = this.findPurchaseItem(purchaseItems, 'gift');
      const activeButton = trigger || this.querySelector(`[data-lf-action="${mode}"]`);
      this.setBusy(true, activeButton);
      this.setStatus(mode === 'buy' ? 'Checkout wird vorbereitet...' : 'Wird zum Warenkorb hinzugefügt...');
      const sections = this.getSections();
      try {
        if (mode === 'buy') {
          const checkoutUrl = await this.createIsolatedCheckout(purchaseItems);
          this.setStatus('Weiterleitung zum Checkout...', 'success');
          window.location.assign(checkoutUrl);
          return;
        }

        const baselineCart = selectedGift ? await this.fetchCart() : null;
        const added = await this.postJson(this.dataset.cartAddUrl, {
          items: this.buildCartItems(purchaseItems),
          sections,
          sections_url: window.location.pathname
        });
        const giftLineKey = this.findCartItem(added, selectedGift)?.key || '';
        let renderedState = added;
        let discountError = '';
        const codes = this.pendingPromotionCodes();
        if (codes.length) {
          try {
            renderedState = await this.postJson(`${this.getBasePath()}cart/update.js`, {
              discount: codes.join(','),
              sections,
              sections_url: window.location.pathname
            });
          } catch (error) {
            discountError = error.message || 'Der Rabatt konnte nicht angewendet werden.';
          }
        }
        const mbcSynced = selectedGift ? await this.syncMbcCart(selectedGift) : false;
        if (mbcSynced) {
          const refreshedState = await this.refreshCartSections(sections);
          if (refreshedState) renderedState = refreshedState;
        }
        this.renderSections(renderedState.sections || added.sections);
        let cart;
        let cartVerificationLimited = false;
        try {
          cart = await this.fetchCart(selectedGift, baselineCart, mbcSynced ? 4 : 1);
        } catch (error) {
          const fallbackState = Array.isArray(renderedState?.items) ? renderedState : added;
          if (!selectedGift || !Array.isArray(fallbackState?.items)) {
            this.setStatus('Zum Warenkorb hinzugefügt. Der endgültige Gesamtbetrag konnte noch nicht überprüft werden.', 'warning');
            this.openCartDrawer();
            return;
          }
          cart = fallbackState;
          cartVerificationLimited = true;
        }
        const unavailableCodes = this.syncPromotionStatuses(cart);
        const discountProblem = discountError || (unavailableCodes.length ? `${unavailableCodes.join(', ')} kann auf diesen Warenkorb nicht angewendet werden.` : '');
        const giftVerified = this.verifyGift(cart, selectedGift, baselineCart);
        emitDocumentEvent('cart:item-added', {
          product: added.items?.find((item) => String(item.id) === String(this.currentVariant.id)) || added.items?.[0],
          items: added.items || [],
          source: 'lf-product-purchase'
        });

        if (selectedGift && !giftVerified) {
          if (cartVerificationLimited) {
            this.giftVerificationFailed = true;
            this.querySelector('lf-product-gifts')?.setVerifiedState('failed');
            this.updateTotals();
            this.setStatus('Das Geschenk konnte nicht vollständig überprüft werden. Der Warenkorb blieb daher unverändert. Bitte prüfen Sie den Warenkorb vor dem Checkout.', 'warning');
            this.openCartDrawer();
            return;
          }

          let giftRemoved = false;
          try {
            const rollback = await this.restoreGiftQuantity(cart, baselineCart, selectedGift, giftLineKey, sections);
            giftRemoved = rollback.restored;
            if (rollback.cart?.sections) this.renderSections(rollback.cart.sections);
            if (giftRemoved) await this.syncMbcCart(selectedGift);
          } catch (error) {
            console.error('Die Menge des nicht rabattierten Geschenks konnte nicht wiederhergestellt werden.', error);
          }

          if (giftRemoved) {
            this.giftItem = null;
            this.giftVerificationFailed = false;
            this.querySelector('lf-product-gifts')?.clearSelection('failed', false);
            this.updateTotals();
            this.setStatus('Das ausgewählte Geschenk war nicht kostenlos und wurde deshalb aus dem Warenkorb entfernt.', 'warning');
          } else {
            this.giftVerificationFailed = true;
            this.querySelector('lf-product-gifts')?.setVerifiedState('failed');
            this.updateTotals();
            this.setStatus('Das Geschenk wurde nicht rabattiert und konnte nicht automatisch entfernt werden. Bitte prüfen Sie den Warenkorb vor dem Checkout.', 'warning');
          }
          this.openCartDrawer();
          return;
        }

        if (cartVerificationLimited) {
          this.setStatus('Zum Warenkorb hinzugefügt. Das Geschenk scheint kostenlos zu sein, der endgültige Gesamtbetrag konnte jedoch noch nicht vollständig überprüft werden.', 'warning');
          this.openCartDrawer();
          return;
        }

        if (selectedGift) this.querySelector('lf-product-gifts')?.setVerifiedState('verified');
        this.giftVerificationFailed = false;
        this.updateTotals();
        this.setStatus(discountProblem ? `Zum Warenkorb hinzugefügt. ${discountProblem}` : 'Zum Warenkorb hinzugefügt.', discountProblem ? 'warning' : 'success');
        this.openCartDrawer();
      } catch (error) {
        console.error(error);
        const message = error.message || (mode === 'buy'
          ? 'Der Checkout für diese Auswahl konnte nicht vorbereitet werden. Bitte versuchen Sie es erneut.'
          : 'Diese Auswahl konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut.');
        if (this.isInventoryError(message)) this.querySelector('lf-product-addons')?.showServerInventoryError(message);
        this.setStatus(message, 'error');
      } finally {
        this.setBusy(false);
      }
    }

    verifyGift(cart, giftItem, baselineCart = null) {
      if (!giftItem) return true;
      const current = this.cartVariantStats(cart, giftItem);
      const baseline = this.cartVariantStats(baselineCart, giftItem);
      const expectedQuantity = Math.max(1, Math.floor(toNumber(giftItem.quantity, 1)));
      return current.quantity >= baseline.quantity + expectedQuantity
        && current.freeQuantity >= baseline.freeQuantity + expectedQuantity;
    }

    syncPromotionStatuses(cart) {
      const discountCodes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
      const unavailable = [];
      this.querySelectorAll('[data-lf-promo]').forEach((promotion) => {
        const code = promotion.dataset.code;
        const status = promotion.querySelector('[data-lf-promo-status]');
        if (!code || !this.isPromotionQueued(promotion) || !status) return;
        const match = discountCodes.find((entry) => String(entry.code).toLowerCase() === code.toLowerCase());
        status.classList.remove('is-error', 'is-applied');
        if (match && match.applicable === false) {
          unavailable.push(code);
          status.textContent = `${code} kann auf den aktuellen Warenkorb nicht angewendet werden.`;
          status.classList.add('is-error');
        } else {
          status.textContent = match ? `${code} wurde im Warenkorb angewendet.` : `${code} ist vorgemerkt. Shopify bestätigt den Code beim Checkout.`;
          status.classList.add('is-applied');
        }
      });
      return unavailable;
    }

    renderSections(sections) {
      if (!sections) return;
      const definitions = [
        { key: 'main-cart', id: 'Cart', selector: '.thb-cart-form' },
        { key: 'cart-drawer', id: 'Cart-Drawer', selector: '.cart-drawer' },
        { key: 'cart-bubble', id: 'cart-drawer-toggle', selector: '.thb-item-count' }
      ];
      definitions.forEach((definition) => {
        const html = sections[definition.key];
        const root = document.getElementById(definition.id);
        if (!html || !root) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector(definition.selector);
        const target = root.matches(definition.selector) ? root : (root.querySelector(definition.selector) || root);
        if (source) target.innerHTML = source.innerHTML;
      });
      this.refreshCartDrawerControls();
    }

    refreshCartDrawerControls() {
      const drawer = document.getElementById('Cart-Drawer');
      if (!drawer) return;

      const freeShipping = drawer.querySelector('.free-shipping');
      if (freeShipping) {
        const total = toNumber(freeShipping.dataset.cartTotal);
        const currencyRate = toNumber(window.Shopify?.currency?.rate, 1);
        const minimum = Math.round(toNumber(freeShipping.dataset.minimum) * currencyRate);
        const progress = minimum > 0 ? Math.min(1, total / minimum) : 1;
        freeShipping.style.setProperty('--percentage', progress);
      }

      const terms = drawer.querySelector('#CartDrawerTerms');
      if (terms) terms.setCustomValidity(window.theme?.strings?.requiresTerms || 'Bitte akzeptieren Sie die Allgemeinen Geschäftsbedingungen.');
      if (drawer.dataset.lfPurchaseControlsBound === 'true') return;
      drawer.dataset.lfPurchaseControlsBound = 'true';

      drawer.addEventListener('click', (event) => {
        const remove = event.target.closest('.remove[data-index]');
        if (remove && drawer.contains(remove)) {
          event.stopPropagation();
          const quantity = drawer.querySelector(`.qty[data-index="${CSS.escape(remove.dataset.index)}"]`);
          if (quantity) {
            event.preventDefault();
            quantity.value = '0';
            quantity.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        const noteToggle = event.target.closest('#order-note-toggle');
        if (noteToggle && drawer.contains(noteToggle)) {
          event.stopPropagation();
          noteToggle.nextElementSibling?.classList.add('active');
          return;
        }

        const noteClose = event.target.closest('.order-note-toggle__content .button, .order-note-toggle__content-overlay');
        if (noteClose && drawer.contains(noteClose)) {
          event.stopPropagation();
          const notePanel = noteClose.closest('.order-note-toggle__content');
          notePanel?.classList.remove('active');
          const note = drawer.querySelector('#mini-cart__notes')?.value || '';
          this.postJson(`${this.getBasePath()}cart/update.js`, { note }).catch((error) => console.error('Cart note could not be saved.', error));
          return;
        }

        const checkout = event.target.closest('.button.checkout');
        const currentTerms = drawer.querySelector('#CartDrawerTerms');
        if (checkout && currentTerms && !currentTerms.checked) {
          event.stopPropagation();
          event.preventDefault();
          currentTerms.reportValidity();
          currentTerms.focus();
        }
      }, true);
    }

    openCartDrawer({ fallbackToCart = false } = {}) {
      const drawer = document.getElementById('Cart-Drawer');
      if (!drawer) {
        if (fallbackToCart) window.location.assign(this.dataset.cartUrl || `${this.getBasePath()}cart`);
        return false;
      }
      document.body.classList.add('open-cc', 'open-cart');
      drawer.classList.add('active');
      drawer.focus();
      emitDocumentEvent('cart-drawer:open', { source: 'lf-product-purchase' });
      return true;
    }
  }

  const definitions = [
    ['lf-product-gallery', LFProductGallery],
    ['lf-variant-picker', LFVariantPicker],
    ['lf-product-addons', LFProductAddons],
    ['lf-product-gifts', LFProductGifts],
    ['lf-product-nav', LFProductNav],
    ['lf-sticky-purchase', LFStickyPurchase],
    ['lf-product-purchase', LFProductPurchase]
  ];
  definitions.forEach(([name, constructor]) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  });
})();
